import React, { useCallback, useMemo, useState, type FC, useEffect } from 'react'
import { Layout, Menu, Button, type MenuProps, Breadcrumb, Watermark, Tooltip, Dropdown, message } from 'antd'
import useRecoveredMenuState from '@/hooks/useRecoverMenuState'
import {
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  QuestionCircleOutlined,
  DownOutlined
} from '@ant-design/icons'
// import { type BreadcrumbItemType } from 'antd/lib/breadcrumb/Breadcrumb'
import { generateMenuItems } from '@/utils/generateMenuItems'
import { menuList } from '@/routes/menuLIst.config'
import { useNavigate } from 'react-router-dom'
import { searchPath } from '@/routes/route_utils'
import { useRequest } from 'ahooks'
import { useInterface } from '@/context/InterfaceContext'
import * as httpSettings from '@/config/httpSettings'
import HyStore from '@/utils/storage'
import styled from 'styled-components'
import OyNotification from '../OyNotification'
import './index.scss'

const StyledBreadcrumb = styled(Breadcrumb)`
  .ant-breadcrumb-link {
    color: #1677ff !important;
    pointer-events: none !important;
    .anticon {
      pointer-events: auto !important;
    }
    &:hover {
      background-color: transparent !important;
    }
  }
  .ant-breadcrumb-link:hover {
    background-color: transparent !important;
  }
  .ant-breadcrumb ol li:last-child .ant-breadcrumb-link:hover {
    background-color: transparent !important;
  }
`
interface RootLayoutProps {
  children?: React.ReactNode
}

const hyLocalStoreUserName = new HyStore(httpSettings.UserKeyPosition)
const hyLocalStoreUser = new HyStore(httpSettings.UserRolesKeyPosition)
const hyLocalMobile = new HyStore(httpSettings.MobileKeyPosition)
const hyLocalDataAuthorities = new HyStore(httpSettings.UserDataAuthoritiesKeyPosition)

const { Header, Content, Footer, Sider } = Layout

const RootLayout: FC<RootLayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false)

  const Roles = hyLocalStoreUser.getItem(httpSettings.UserRolesKey)
  const currentRoleId = hyLocalStoreUser.getItem(httpSettings.UserCurrentRoleIdKey)
  const dataAuthorities = hyLocalDataAuthorities.getItem(httpSettings.UserDataAuthoritiesKey)
  const menuItems = generateMenuItems(menuList)
  const navigate = useNavigate()
  const UserName = hyLocalStoreUserName.getItem(httpSettings.UserKey)
  const UserMobile = hyLocalMobile.getItem(httpSettings.UserMobile)
  // 处理水印显示逻辑，只显示手机号后四位
  let WatermarkContent = ''
  if (UserMobile) {
    if (UserMobile && UserMobile.length >= 4) {
      // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
      WatermarkContent = '欧冶科技-' + UserMobile.slice(-4)
    } else {
      // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
      WatermarkContent = '欧冶科技-' + UserMobile
    }
  }
  const { selectedKeys, openKeys, breadCrumb, setOpenKeys, setSelectedKeys } = useRecoveredMenuState()
  const isStatement = breadCrumb?.map((item) => item.title)[0]

  const OutLoginApis = useInterface('Login')
  const [menuLists, setMenuLists] = useState<[]>([])
  const getMenuApis = useInterface('GetMenu')

  // 角色切换API
  const { run: changeCurrentRole, loading: switchingRole } = useRequest(
    OutLoginApis.changeCurrentRole.bind(OutLoginApis),
    {
      manual: true,
      onSuccess: (data, params) => {
        // 检查返回的code是否为0才算成功
        if (data.code === 0) {
          // 从 dataAuthorities 中找到目标角色
          const targetRole = dataAuthorities?.find((authority: any) => authority.role_id === params[0].roleId)

          if (targetRole) {
            // 更新本地存储中的角色信息
            hyLocalStoreUser.setItem(httpSettings.UserRolesKey, targetRole.role_name)
            hyLocalStoreUser.setItem(httpSettings.UserCurrentRoleIdKey, targetRole.role_id)
            hyLocalMobile.setItem(httpSettings.UserMobile, targetRole.user_mobile)
            const hyLocalOffice = new HyStore(httpSettings.UserOfficeKeyPosition)
            const hyLocalUserId = new HyStore(httpSettings.UserIdKeyPosition)
            hyLocalOffice.setItem(httpSettings.UserHeadOfficeKey, targetRole.permission_code)
            hyLocalStoreUserName.setItem(httpSettings.UserKey, targetRole.user_name)
            hyLocalUserId.setItem(httpSettings.UserIdKey, targetRole.user_id)
          }

          void message.success('角色切换成功')
          // 刷新页面应用新的角色权限
          setTimeout(() => {
            window.location.reload()
          }, 500)
        } else {
          console.error('角色切换失败:', data.message)
          void message.error(data.message || '角色切换失败')
        }
      },
      onError: (err) => {
        console.error('角色切换API调用失败:', err)
      }
    }
  )

  // 角色切换方法
  const handleRoleSwitch = useCallback(
    (roleId: number) => {
      // 从 dataAuthorities 中找到目标角色
      const targetRole = dataAuthorities?.find((authority: any) => authority.role_id === roleId)

      if (targetRole) {
        // 先调用角色切换API
        changeCurrentRole({ roleId })

        // 成功后会在API成功回调中更新本地存储和刷新页面
      } else {
        console.error('未找到目标角色:', roleId)
        void message.error('未找到目标角色')
      }
    },
    [dataAuthorities, changeCurrentRole]
  )

  // 生成角色下拉菜单项
  const roleMenuItems = useMemo(() => {
    if (!dataAuthorities || dataAuthorities.length === 0) {
      return []
    }

    const items = dataAuthorities.map((authority: any) => ({
      key: authority.role_id,
      disabled: authority.role_id === currentRoleId || switchingRole,
      label: (
        <span style={{ color: authority.role_id === currentRoleId ? '#1677ff' : undefined, fontSize: '12px' }}>
          {authority.permission_code !== 'all'
            ? `${authority.role_name as string} - ${authority.permission_code as string}`
            : authority.role_name === 'OY管理员'
            ? '全部银行'
            : authority.role_name}
        </span>
      ),
      onClick: () => {
        if (authority.role_id !== currentRoleId && !switchingRole) {
          handleRoleSwitch(authority.role_id)
        }
      }
    }))

    return items
  }, [dataAuthorities, currentRoleId, handleRoleSwitch, switchingRole])

  const { run: getMenu } = useRequest(getMenuApis.getMenu.bind(getMenuApis), {
    manual: true,
    onSuccess: (data) => {
      if (data !== null) {
        const res = data.data
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        const { menuList } = res
        const resMenu = []
        if (menuList && menuList.length > 0) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // menuList.push({ permissionName: '攻击情报' }, { permissionName: '行业智库' }, { permissionName: '洞见' })
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          menuList.map((item) => {
            if (item.permissionName === '转账详情信息') {
              item.permissionName = '转账详情'
            }
            if (item.permissionName === '对公账户' && item.permissionUrl.includes('/publicTextAccount/exportExcel')) {
              item.permissionName = '对公账户导出'
            }
            return item
          })
          for (let i = 0; i < menuItems.length; i++) {
            // 特殊处理版本发布中心，不需要权限检查
            if (
              menuItems[i] &&
              (menuItems[i] as any).label &&
              ((menuItems[i] as any).label === '版本发布中心' || (menuItems[i] as any).label === '省份排名')
            ) {
              resMenu.push(menuItems[i])
              continue
            }

            for (let j = 0; j < menuList.length; j++) {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-expect-error
              if (menuList[j].permissionName === menuItems[i].label) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error
                if (menuItems[i].children && menuItems[i].children.length > 0) {
                  const tempChildren = []
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-expect-error
                  for (let k = 0; k < menuItems[i].children.length; k++) {
                    for (let l = 0; l < menuList.length; l++) {
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-expect-error
                      if (menuList[l].permissionName === menuItems[i].children[k].label) {
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-expect-error
                        tempChildren.push(menuItems[i].children[k])
                      }
                    }
                  }
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-expect-error
                  menuItems[i].children = tempChildren
                }

                resMenu.push(menuItems[i])
              }
            }
          }
        }
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        setMenuLists(resMenu)
      }
    },
    onError: (err) => {
      console.error(err)
    }
  })

  useEffect(() => {
    getMenu({})
  }, [])
  // const breadCrumbItems = useMemo(() => {
  //   const home = [
  //     {
  //       href: '/',
  //       title: <HomeOutlined />
  //     }
  //   ] as BreadcrumbItemType[]
  //   return home.concat(breadCrumb ?? [])
  // }, [breadCrumb])

  const breadCrumbItems = useMemo(() => {
    const items = breadCrumb ?? []
    return items.map((item: any) => {
      if (item.title === '趋势分析 / 银行活跃指数') {
        return {
          ...item,
          title: (
            <span>
              趋势分析 / 银行活跃指数
              <Tooltip title="银行活跃指数是指，在特定范围内每一万个涉及非法活动的群组中，各银行机构被提及或出现的频次。该指数用于量化评估各银行机构在互联网中的活跃状态及市场渗透力。">
                <QuestionCircleOutlined style={{ marginLeft: 4, cursor: 'pointer' }} />
              </Tooltip>
            </span>
          )
        }
      }
      return item
    })
  }, [breadCrumb])

  const navigateTo = useCallback(
    (id: string) => {
      const path = searchPath(id)
      navigate(path)
    },
    [navigate]
  )

  const handleNavigate = useCallback<Exclude<MenuProps['onClick'], undefined>>(
    (sel) => {
      setSelectedKeys(sel.keyPath)
      navigateTo(sel.key)
    },
    [navigateTo, setSelectedKeys]
  )

  const handleOpenChange = useCallback<Exclude<MenuProps['onOpenChange'], undefined>>(
    (keys) => {
      setOpenKeys(keys)
    },
    [setOpenKeys]
  )

  const { run: outLogin } = useRequest(OutLoginApis.outLogin.bind(OutLoginApis), {
    manual: true,
    onSuccess: ({ data }) => {
      // toHomePage()
    }
  })

  const onLogoutClick = useCallback(() => {
    outLogin({})
    window.location.href = process.env.NODE_ENV !== 'production' ? '/dev/user/login' : '/user/login'
    localStorage.removeItem('x-oy-user-roles')
    localStorage.removeItem('x-oy-head-office')
    localStorage.removeItem('x-oy-token')
    localStorage.removeItem('x-oy-mobile')
    localStorage.removeItem('x-oy-user-id')
    setTimeout(function () {
      localStorage.removeItem('x-oy-user')
    }, 1000)
  }, [])

  return (
    <Watermark height={25} content={WatermarkContent}>
      <Layout style={{ minHeight: '100vh' }}>
        <Sider collapsible={false} collapsed={collapsed} style={{ position: 'fixed', height: '100%', zIndex: 100 }}>
          <div
            style={{
              height: '48px',
              width: '90%',
              color: 'white',
              marginBottom: '14px',
              borderRadius: '10px',
              position: 'relative',
              left: '-2px',
              top: '14px'
            }}
          >
            <span style={{ position: 'relative', left: '26px', top: '4px' }}>
              <img src="/logo.png" className="logo-img" style={{ width: '40px' }} />
            </span>
            {collapsed ? (
              ''
            ) : (
              <span
                style={{
                  position: 'relative',
                  fontSize: '20px',
                  top: '-6px',
                  left: '38px',
                  fontWeight: 900
                }}
              >
                连弩系统
              </span>
            )}
          </div>
          <div
            className="menu-container-no-scrollbar"
            style={{
              height: 'calc(100% - 65px)',
              overflow: 'auto',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}
          >
            <Menu
              theme="dark"
              selectedKeys={selectedKeys}
              openKeys={openKeys}
              items={
                Roles === 'OY管理员'
                  ? menuLists
                  : menuLists
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-expect-error
                      .filter((item) => item.label !== '信息配置' && item.label !== '操作日志')
                      .map((item) => {
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-expect-error
                        const copy = { ...item }
                        return copy
                      })
              }
              onClick={handleNavigate}
              onOpenChange={handleOpenChange}
              mode="inline"
            />
          </div>
          <div
            style={{
              position: 'absolute',
              bottom: '0',
              width: '100%',
              textAlign: 'center',
              zIndex: 900
            }}
          >
            {collapsed ? (
              <MenuUnfoldOutlined
                style={{ color: '#fff', fontSize: '20px', position: 'relative', left: '0' }}
                onClick={() => {
                  setCollapsed(false)
                }}
              />
            ) : (
              <MenuFoldOutlined
                style={{ color: '#fff', fontSize: '20px', position: 'relative', left: '72px' }}
                onClick={() => {
                  setCollapsed(true)
                }}
              />
            )}
          </div>
        </Sider>
        <Layout style={{ marginLeft: collapsed ? '80px' : '200px' }}>
          <Header style={{ padding: 0, background: '#fff', textAlign: 'right', display: 'flex' }}>
            <StyledBreadcrumb
              items={breadCrumbItems}
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-expect-error
              onClick={(e: any) => e.preventDefault()}
              style={{
                position: 'relative',
                top: '2.6vh',
                left: '1vw',
                flex: 1,
                pointerEvents: 'none'
              }}
            ></StyledBreadcrumb>
            <div style={{ flex: 4 }}>
              {isStatement === '卡证数据源 / 高危卡号' ||
              isStatement === '卡证数据源 / 高危身份证' ||
              isStatement === '卡证数据源 / 高危卡号 / 原始消息' ||
              isStatement === '卡证数据源 / 高危身份证 / 原始消息' ||
              isStatement === '卡证数据源 / 转账详情 / 原始消息' ? (
                <span
                  style={{
                    textAlign: 'right',
                    fontSize: 12,
                    fontWeight: 'normal',
                    color: 'rgba(0, 0, 0, 0.45)',
                    position: 'relative',
                    right: '1vw'
                  }}
                >
                  声明：本页面信息源自互联网，仅供银行防控参考，不做任何商业用途
                </span>
              ) : (
                ''
              )}

              <Dropdown
                menu={{
                  items: roleMenuItems,
                  style: { minWidth: 'auto' }
                }}
                trigger={['hover']}
                placement="bottom"
                overlayStyle={{ minWidth: 'auto', width: 'auto' }}
                disabled={switchingRole}
              >
                <Button
                  icon={<UserOutlined style={{ color: '#1677ff', fontSize: '20px', cursor: 'pointer' }} />}
                  type="link"
                  loading={switchingRole}
                  disabled={switchingRole}
                  style={{
                    color: '#1677ff',
                    marginRight: '-6px',
                    cursor: switchingRole ? 'not-allowed' : 'pointer',
                    position: 'relative',
                    top: '-1px'
                  }}
                >
                  {UserName} <DownOutlined style={{ fontSize: '12px' }} />
                </Button>
              </Dropdown>
              <Button
                type="text"
                style={{
                  color: 'rgba(0, 0, 0, 0.45)',
                  padding: '4px',
                  margin: '8px',
                  right: '10px'
                }}
                onClick={onLogoutClick}
              >
                退出登录 <LogoutOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
              </Button>
            </div>
          </Header>
          <Content style={{ margin: '12px 16px' }}>
            {/* <Breadcrumb
            items={breadCrumbItems}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            onClick={(e: any) => e.preventDefault()}
            style={{ position: 'relative', top: '-4vh' }}
          /> */}

            {isStatement?.toString().includes('情报线索') ? (
              <div
                style={{
                  padding: '0 24px 24px 24px',
                  minHeight: 360,
                  background: '#fff',
                  borderRadius: '8px',
                  position: 'relative'
                }}
              >
                {children}
              </div>
            ) : isStatement?.toString().includes('地域洞察') ? (
              <div
                style={{
                  background: '#fff',
                  borderRadius: '8px',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                className="main-con-div"
              >
                {children}
              </div>
            ) : (
              <div
                style={{
                  minHeight: 'calc(100vh - 180px)',
                  background: '#fff',
                  borderRadius: '8px',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                className="main-con-div"
              >
                {children}
              </div>
            )}
          </Content>

          <Footer style={{ textAlign: 'center', color: 'rgba(0, 0, 0, 0.45)' }}>
            ©版权所有 北京欧冶上善科技有限公司<span style={{ marginRight: '25px' }}></span>
            地址：北京市海淀区首享科技大厦9层901室<span style={{ marginRight: '25px' }}></span>
            邮箱：oyservice@ouye.tech
          </Footer>

          <OyNotification />
        </Layout>
      </Layout>
    </Watermark>
  )
}

export default RootLayout
